import { useAccount } from "@qw/auth";
import { isInternal } from "@qw/qw-common";
import { AppSwitcher, ColumnHeader } from "@qw/ui";
import type { FC, PropsWithChildren, ReactNode } from "react";
import { Link } from "react-router";
import { AlbyNavMenu } from "./alby-nav-menu";
import { UserMenu } from "./user-menu";

type AppNavProps = PropsWithChildren<{
  center?: ReactNode;
}>;

// The `z-20` on both headers is load-bearing. `ColumnHeader` is `sticky z-2`, so this nav is a
// stacking context and the search box's dropdown cannot escape it however high its own z-index
// goes — and every column header inside `<main>` ties the default `z-2` and wins on document order,
// painting over the dropdown. The nav is the app's outermost chrome, so it outranks in-content
// headers (`z-2`, `z-10`) while staying under the chat dock (`z-30`) and composer (`z-40`).
export const AppNav: FC<AppNavProps> = ({ center, children }) => {
  const { user } = useAccount();
  const internal = isInternal(user);

  const rightControls = (
    <div className="flex items-center gap-2">
      <AlbyNavMenu />
      <AppSwitcher currentApp="inbox" isInternal={internal} LinkComponent={Link} />
    </div>
  );

  if (center) {
    return (
      <ColumnHeader className="bg-background z-20">
        <div className="flex min-w-0 flex-1 basis-0 items-center gap-2">
          <UserMenu />
          {children}
        </div>
        <div className="flex min-w-0 flex-[2_1_0%] items-center justify-center px-2">
          <div className="flex w-full max-w-xl min-w-0 items-center gap-2">{center}</div>
        </div>
        <div className="flex min-w-0 flex-1 basis-0 items-center justify-end">{rightControls}</div>
      </ColumnHeader>
    );
  }

  return (
    <ColumnHeader className="bg-background z-20">
      <div className="flex items-center gap-2">
        <UserMenu />
        {children}
      </div>
      <div className="flex-auto" />
      {rightControls}
    </ColumnHeader>
  );
};
